import { useEffect, useRef } from 'react';

/**
 * Detect when a new frontend build has been deployed.
 *
 * Strategy:
 *  - On mount, capture the *current* main.js URL from <script src="/static/js/main.*.js">.
 *  - Every `intervalMs` (default 60s), fetch /asset-manifest.json (cache-busted) — a file
 *    that Create-React-App generates automatically at build time. It contains the new
 *    hashed filename of main.js after each deploy.
 *  - If the main.js URL in the manifest no longer matches the one currently loaded,
 *    a new version is live → invoke `onNewVersion()`.
 *
 * This works on production builds. In dev mode CRA doesn't expose asset-manifest,
 * so the hook silently no-ops.
 */
export default function useVersionCheck(onNewVersion, intervalMs = 60000) {
  const currentScriptRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    // Only run in production builds where /asset-manifest.json exists
    if (process.env.NODE_ENV !== 'production') return;

    // Capture the *current* main.js URL (e.g. /static/js/main.f71949df.js)
    const scripts = Array.from(document.querySelectorAll('script[src*="/static/js/main."]'));
    const current = scripts.length ? scripts[scripts.length - 1].getAttribute('src') : null;
    if (!current) return;
    currentScriptRef.current = current.replace(/^https?:\/\/[^/]+/, '');

    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const manifest = await res.json();
        const latest = manifest?.files?.['main.js'];
        if (!latest) return;
        if (!cancelled && !notifiedRef.current && latest !== currentScriptRef.current) {
          notifiedRef.current = true;
          onNewVersion();
        }
      } catch (_) {
        // Network error — ignore, will retry next interval
      }
    };

    // First check after a small delay (don't spam right at load)
    const initial = setTimeout(check, 10000);
    const id = setInterval(check, intervalMs);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(id);
    };
  }, [onNewVersion, intervalMs]);
}
